import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import {
  MediaTypesEnum,
  bytesToMBytes,
  bytesToKBytes,
  DATE_FORMAT,
} from "dash-common";
import { Box, Flex, Text, Checkbox } from "dash-ui";

import { RoutesPath } from "flow-components/routes/routes";

import {
  MediaFilterItemWrapper,
  ImageWrapper,
  StyledTopContainer,
  Overlay,
} from "./MediaFileItem.styles";
import ViewFileContainer from "./components/ViewFileContainer";
import ActionButtons from "./components/ActionButtons";
import { IMediaFileItemProps } from "./MediaFileItem.interfaces";
import { useContext } from "react";
import { BulkActionsContext } from "common/contexts/bulkActionsContext/bulkActionsContext";
import BrandingTypeBadge from "containers/properties/PropertyMedia/components/BrandingTypeBadge/BrandingTypeBadge";

const MediaFileItem = ({
  media,
  onClick,
  deletable = true,
  selectItemProps,
}: IMediaFileItemProps) => {
  const { t } = useTranslation(["common", "pages"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const isFolder = media.type === MediaTypesEnum.FOLDER;
  const { resetState } = useContext(BulkActionsContext);

  const onMediaClick = () => {
    if (isFolder) {
      resetState();
      return navigate(
        RoutesPath.PropertyMediaFolder.replace(":id", media.propertyId).replace(
          ":folderId",
          media._id
        )
      );
    }

    if (onClick) {
      onClick();
    }
  };

  return (
    <MediaFilterItemWrapper w={230} h={210}>
      <Overlay onClick={onMediaClick} />
      <ImageWrapper h={112} justifyContent="center" alignItems="center">
        <ViewFileContainer isFolder={isFolder} media={media} />
      </ImageWrapper>
      <Box p={2} overflow="hidden">
        <Box mb={1}>
          <Text ellipsis medium color={theme.colors.n80} bold>
            {isFolder
              ? `${t("pages:propertyPage.mediaItemsCount", {
                  itemsCount: media.mediaItemsCount,
                })} ${media.folderName}`
              : media.files.length && media.files[0].name}
          </Text>
        </Box>

        <Flex alignItems="center" gap={2} mb={1}>
          {!isFolder && (
            <Text color={theme.colors.n80} sm medium>
              {media.files.length
                ? media.files[0].publicUrl.split(".").pop()?.toUpperCase()
                : ""}
            </Text>
          )}
          <Box ml={2}>
            <BrandingTypeBadge
              brandingType={media.brandingType as any}
              systemFolderType={media.systemFolderType as any}
              isFolder={isFolder}
            />
          </Box>
        </Flex>
        <Flex alignItems="center" justifyContent="space-between" mt={2}>
          <Text medium color={theme.colors.n80} sm>
            {moment(media.updatedAt).format(DATE_FORMAT)}
          </Text>
          <Text medium color={theme.colors.n80} sm>
            {bytesToMBytes(media.sizeInBytes) > 1
              ? t("common:labels.mBytes", {
                  size: bytesToMBytes(media.sizeInBytes),
                })
              : t("common:labels.kBytes", {
                  size: bytesToKBytes(media.sizeInBytes),
                })}
          </Text>
        </Flex>
      </Box>
      <StyledTopContainer>
        {!isFolder &&
          (selectItemProps ? (
            <Checkbox
              onChange={selectItemProps.onSelect}
              checked={selectItemProps.checked}
            />
          ) : null)}
      </StyledTopContainer>
      <ActionButtons media={media} deletable={deletable} />
    </MediaFilterItemWrapper>
  );
};

export default MediaFileItem;
